import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, UserRole } from '../types'
import { simpleDirectCloudApi } from '../services/directCloudApi.simple'
import { auth } from '../services/tcbClient'

interface AuthState {
  user: User | null
  isLoading: boolean
  login: (username: string, password: string) => Promise<void>
  logout: () => void
  checkPermission: (permission: string) => boolean
  hasRole: (role: UserRole) => boolean
  checkRouteAccess: (route: string) => boolean
  getDefaultRoute: () => string
}

const normalizeRole = (role: any): UserRole => {
  const raw = String(role || '').toLowerCase()
  if (raw === 'a' || raw === 'b' || raw === 'c' || raw === 'd') return raw.toUpperCase() as UserRole
  if (raw === 'boss') return 'A'
  if (raw === 'cashier') return 'A'
  if (raw === 'parts') return 'D'
  if (raw === 'accountant') return 'B'
  if (raw === 'vehicle_manager') return 'B'
  return 'C'
}

const convertCloudUserToLocal = (cloudUser: any): User => {
  const normalizedRole = normalizeRole(cloudUser.role)
  const preferredName =
    cloudUser.nickname ||
    cloudUser.nickName ||
    cloudUser.displayName ||
    cloudUser.username ||
    cloudUser._id ||
    cloudUser.id ||
    'user'
  return {
    id: cloudUser._id || cloudUser.id || cloudUser.uid || cloudUser.nickName,
    nickName: preferredName,
    nickname: cloudUser.nickname,
    info: cloudUser.info,
    username: cloudUser.username,
    avatarUrl: cloudUser.avatarUrl || getDefaultAvatar(normalizedRole),
    role: normalizedRole,
    createTime: cloudUser.createTime,
    loginTime: new Date().toISOString(),
    isActive: cloudUser.isActive !== false
  }
}

const getDefaultAvatar = (role: string): string => {
  const avatars = {
    A: 'A',
    B: 'B',
    C: 'C',
    D: 'D'
  }
  const normalized = String(role || '').toUpperCase()
  return avatars[normalized as keyof typeof avatars] || 'USER'
}

const ROLE_PERMISSIONS = {
  A: [
    'view_vehicles',
    'manage_vehicles',
    'manage_reminders',
    'confirm_payment',
    'update_payment_amount',
    'view_pending_accounting',
    'upload_vouchers',
    'confirm_accounting',
    'view_accounting_dashboard',
    'view_payment_confirmations',
    'submit_payment_info',
    'verify_accounting',
    'view_cashier_dashboard',
    'view_rent_records',
    'view_boss',
    'view_vehicle_adjustments',
    'manage_vehicle_adjustments'
  ],
  B: [
    'view_vehicles',
    'manage_vehicles',
    'manage_reminders',
    'confirm_payment',
    'update_payment_amount',
    'view_pending_accounting',
    'upload_vouchers',
    'confirm_accounting',
    'view_accounting_dashboard'
  ],
  C: [
    'view_vehicles'
  ],
  D: [
    'view_vehicle_adjustments',
    'manage_vehicle_adjustments'
  ]
} as const

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: false,

      login: async (username: string, password: string) => {
        set({ isLoading: true })

        try {
          if (!username || !password) {
            throw new Error('Username or password is empty')
          }

          // CloudBase username/password sign-in
          console.log('[auth] signIn with username:', username)
          await auth.signIn({
            username,
            password
          })

          // Fetch auth profile first so we can prefer auth nickname in UI.
          const loginState: any = await auth.getLoginState().catch(() => null)
          const authUser = loginState?.user || {}
          const uid =
            authUser?.uid ||
            authUser?.userId ||
            authUser?.id ||
            authUser?.uuid

          // Fetch user profile (role) from DB
          console.log('[auth] lookup user:', username)
          let result = await simpleDirectCloudApi.getUserByIdentity(username)

          if (!result.success || !result.data) {
            if (uid) {
              result = await simpleDirectCloudApi.getUserByIdentity(uid)
            }
            if (!result.success || !result.data) {
              const created = await simpleDirectCloudApi.addUserProfile({
                uid,
                username,
                nickName: username,
                role: 'C'
              })
              if (!created.success) {
                throw new Error(created.error || 'User not found or disabled')
              }
              result = await simpleDirectCloudApi.getUserByIdentity(uid || username)
            }
          }

          if (!result.success || !result.data) {
            throw new Error(result.error || 'User not found or disabled')
          }

          const mergedProfile = {
            ...result.data,
            username: result.data?.username || authUser?.username || username,
            nickname: result.data?.nickname || authUser?.nickname,
            nickName: result.data?.nickName || authUser?.nickName || authUser?.nickname || username,
            info: result.data?.info || authUser?.info
          }
          const user = convertCloudUserToLocal(mergedProfile)
          console.log('[auth] login success', user)

          set({ user, isLoading: false })
        } catch (error) {
          console.error('[auth] login failed', error)
          set({ isLoading: false })
          throw error
        }
      },

      logout: () => {
        auth.signOut().catch(() => {})
        set({ user: null })
      },

      checkPermission: (permission: string) => {
        const { user } = get()
        if (!user) return false

        const rolePermissions = ROLE_PERMISSIONS[user.role]
        return rolePermissions.includes(permission as any)
      },

      hasRole: (role: UserRole) => {
        const { user } = get()
        return user?.role === role
      },

      checkRouteAccess: (route: string) => {
        const { user } = get()
        if (!user) return false

        if (route.startsWith('/vehicles/')) {
          return ['A', 'B', 'C'].includes(user.role)
        }

        const routePermissions: Record<string, UserRole[]> = {
          '/': ['A', 'B', 'C', 'D'],
          '/vehicles': ['A', 'B', 'C'],
          '/vehicle-manager': ['A', 'B', 'C'],
          '/accountant': ['A', 'B'],
          '/cashier': ['A'],
          '/parts': ['A', 'D'],
          '/boss': ['A']
        }

        const allowedRoles = routePermissions[route]
        return allowedRoles ? allowedRoles.includes(user.role) : false
      },

      getDefaultRoute: () => {
        const { user } = get()
        if (!user) return '/login'

        const defaultRoutes: Record<UserRole, string> = {
          A: '/boss',
          B: '/vehicle-manager',
          C: '/vehicle-manager',
          D: '/parts'
        }

        return defaultRoutes[user.role] || '/vehicles'
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user })
    }
  )
)
